import { CheckCircle2, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { type ReactNode } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

export type RunFeedbackLabel = 'good' | 'bad';
export type RunFeedbackPromptState = 'prompt' | 'thanks' | 'note';

// Shared by every thumbs-down note flow (Teach Phoebe feed, home training
// card) so the client-facing copy can't drift between surfaces.
export const RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER =
    'What should Phoebe have done differently? ' +
    'E.g. “Offer Maria first — she’s this client’s regular caregiver.”';

function RunFeedbackIconButton({
    label,
    children,
    disabled,
    onClick,
}: {
    readonly label: string;
    readonly children: ReactNode;
    readonly disabled?: boolean;
    readonly onClick: () => void;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-full"
                    aria-label={label}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {children}
                </Button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
        </Tooltip>
    );
}

/**
 * Shared thumbs-feedback card used by the chat run prompt and the Teach
 * Phoebe review feed: 'prompt' asks for a rating, 'thanks' acknowledges
 * with an optional note entry point, 'note' collects the free-text detail.
 * Fully presentational — callers own state, persistence, and analytics.
 * The thumbs/dismiss handlers are optional for surfaces (like the feed)
 * that provide their own rating entry point and only render the
 * 'thanks'/'note' states. With requireComment, the note text becomes
 * mandatory: Save stays disabled until text is entered and the secondary
 * action reads Cancel instead of Skip.
 */
export function RunFeedbackPrompt({
    state,
    comment,
    disabled,
    className,
    commentPlaceholder,
    requireComment = false,
    autoFocusComment = false,
    submitOnEnter = false,
    onCommentChange,
    onDismiss,
    onSaveComment,
    onSkipComment,
    onShowNote,
    onThumbsDown,
    onThumbsUp,
}: {
    readonly state: RunFeedbackPromptState;
    readonly comment: string;
    readonly disabled: boolean;
    readonly className?: string;
    readonly commentPlaceholder?: string;
    readonly requireComment?: boolean;
    readonly autoFocusComment?: boolean;
    readonly submitOnEnter?: boolean;
    readonly onCommentChange: (comment: string) => void;
    readonly onDismiss?: () => void;
    readonly onSaveComment: () => void;
    readonly onSkipComment: () => void;
    readonly onShowNote: () => void;
    readonly onThumbsDown?: () => void;
    readonly onThumbsUp?: () => void;
}) {
    if (state === 'note') {
        const saveDisabled =
            disabled || (requireComment && comment.trim().length === 0);
        return (
            <div
                className={cn(
                    'bg-background/95 rounded-lg border px-3 py-2 shadow-sm',
                    className,
                )}
            >
                <div className="flex flex-col gap-2">
                    <textarea
                        className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring min-h-16 w-full resize-none rounded-md border px-2 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        rows={requireComment ? 3 : 2}
                        maxLength={1000}
                        placeholder={commentPlaceholder ?? 'Optional details'}
                        value={comment}
                        disabled={disabled}
                        autoFocus={autoFocusComment}
                        onChange={(event) => {
                            onCommentChange(event.target.value);
                        }}
                        onKeyDown={(event) => {
                            if (
                                submitOnEnter &&
                                event.key === 'Enter' &&
                                !event.shiftKey &&
                                !saveDisabled
                            ) {
                                event.preventDefault();
                                onSaveComment();
                            }
                        }}
                    />
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            type="button"
                            size="sm"
                            className="h-8"
                            disabled={saveDisabled}
                            onClick={onSaveComment}
                        >
                            Save
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8"
                            disabled={disabled}
                            onClick={onSkipComment}
                        >
                            {requireComment ? 'Cancel' : 'Skip'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (state === 'thanks') {
        return (
            <div
                className={cn(
                    'bg-background/95 text-foreground flex items-center gap-2 rounded-full border px-3 py-2 text-sm shadow-sm',
                    className,
                )}
            >
                <CheckCircle2 className="text-primary h-4 w-4 shrink-0" />
                <span className="font-medium">Thanks</span>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-7 rounded-full px-2"
                    disabled={disabled}
                    onClick={onShowNote}
                >
                    Add note
                </Button>
                <RunFeedbackIconButton
                    label="Close"
                    disabled={disabled}
                    onClick={onSkipComment}
                >
                    <X className="h-3.5 w-3.5" />
                </RunFeedbackIconButton>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'bg-background/95 text-foreground flex items-center gap-2 rounded-full border px-3 py-2 text-sm shadow-sm',
                className,
            )}
        >
            <span className="min-w-0 truncate font-medium">
                Was Phoebe helpful?
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1">
                {onThumbsUp ? (
                    <RunFeedbackIconButton
                        label="Helpful"
                        disabled={disabled}
                        onClick={onThumbsUp}
                    >
                        <ThumbsUp className="h-3.5 w-3.5" />
                    </RunFeedbackIconButton>
                ) : null}
                {onThumbsDown ? (
                    <RunFeedbackIconButton
                        label="Not helpful"
                        disabled={disabled}
                        onClick={onThumbsDown}
                    >
                        <ThumbsDown className="h-3.5 w-3.5" />
                    </RunFeedbackIconButton>
                ) : null}
                {onDismiss ? (
                    <RunFeedbackIconButton
                        label="Dismiss"
                        disabled={disabled}
                        onClick={onDismiss}
                    >
                        <X className="h-3.5 w-3.5" />
                    </RunFeedbackIconButton>
                ) : null}
            </div>
        </div>
    );
}
